import {
  User,
  Settings,
  LogOut,
  Calendar,
  DollarSign,
  ArrowRight,
  Sparkles,
  BarChart3,
  Users,
  Shield,
} from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface NavigationProps {
  isAuthenticated?: boolean;
}

const Navigation = ({ isAuthenticated = false }: NavigationProps) => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // Get user data from localStorage or session when authenticated
    if (isAuthenticated) {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (e) {
          console.error("Error parsing user data:", e);
        }
      }
    } else {
      setUser(null);
    }
  }, [isAuthenticated]);

  const scrollToSection = (sectionId: string) => {
    // If not on homepage, navigate there first
    if (window.location.pathname !== "/") {
      // Navigate to home page and then scroll to section after navigation
      navigate("/");
      // Use setTimeout to wait for navigation and rendering to complete
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    } else {
      // Already on homepage, just scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  const handleLogout = async () => {
    try {
      // Clear auth token
      localStorage.removeItem("authToken");
      localStorage.removeItem("user");
      // Reload the page to trigger auth state update
      window.location.href = "/";
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const initials =
    user?.full_name
      ?.split(" ")
      .map((n: string) => n[0])
      .join("")
      .toUpperCase() || "U";

  // Check if user is admin
  const isAdmin =
    user?.role === "admin" || import.meta.env.VITE_DEV_MODE === "true";

  return (
    <nav
      className={cn(
        "fixed w-full z-40 transition-all duration-300 bg-white",
        isScrolled ? "shadow-sm border-b" : "",
      )}
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <img
              src="/aig_consulting_logos_v4_darkLogoFull.png"
              alt="AI Genius Automations"
              className="h-16"
            />
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection("process")}
              className="relative text-gray-700 hover:text-purple-600 transition-colors font-medium group"
            >
              <span className="relative">
                Process
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-600 to-indigo-600 group-hover:w-full transition-all duration-300 ease-out" />
              </span>
            </button>

            <button
              onClick={() => scrollToSection("contact")}
              className="relative text-gray-700 hover:text-purple-600 transition-colors font-medium group"
            >
              <span className="relative">
                Contact
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-600 to-indigo-600 group-hover:w-full transition-all duration-300 ease-out" />
              </span>
            </button>

            <button
              onClick={() => scrollToSection("pricing")}
              className="relative text-gray-700 hover:text-purple-600 transition-colors font-medium group"
            >
              <span className="relative">
                Pricing
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-600 to-indigo-600 group-hover:w-full transition-all duration-300 ease-out" />
              </span>
            </button>

            <Link
              to="/calendar"
              className="relative text-gray-700 hover:text-purple-600 transition-colors font-medium group"
            >
              <span className="relative">
                AI Genius Event Calendar
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-600 to-indigo-600 group-hover:w-full transition-all duration-300 ease-out" />
              </span>
            </Link>

            <Link
              to="/case-studies"
              className="relative text-gray-700 hover:text-purple-600 transition-colors font-medium group"
            >
              <span className="relative">
                Case Studies
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-600 to-indigo-600 group-hover:w-full transition-all duration-300 ease-out" />
              </span>
            </Link>

            {isAuthenticated && (
              <Link
                to="/tools"
                className="relative text-gray-700 hover:text-purple-600 transition-colors font-medium group"
              >
                <span className="relative">
                  Tools
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-600 to-indigo-600 group-hover:w-full transition-all duration-300 ease-out" />
                </span>
              </Link>
            )}

            {!isAuthenticated ? (
              <div className="flex items-center space-x-4">
                <Link to="/login">
                  <Button variant="ghost" className="text-gray-700">
                    Sign In
                  </Button>
                </Link>
                <Link to="/signup" aria-label="Sign up for a new account">
                  <Button className="relative overflow-hidden bg-gradient-to-r from-purple-600 via-purple-500 to-indigo-600 hover:from-purple-700 hover:via-purple-600 hover:to-indigo-700 text-white font-semibold px-6 py-2.5 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 group">
                    <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                    <span className="relative flex items-center gap-2">
                      <Sparkles className="h-4 w-4 animate-pulse" />
                      Sign Up
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
                    </span>
                  </Button>
                </Link>
              </div>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="relative h-10 w-10 rounded-full"
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarImage
                        src={user?.avatar_url}
                        alt={user?.full_name}
                      />
                      <AvatarFallback className="bg-purple-100 text-purple-600">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {user?.full_name}
                      </p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user?.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/profile")}>
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/tools")}>
                    <Calendar className="mr-2 h-4 w-4" />
                    <span>Tools</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/pricing")}>
                    <DollarSign className="mr-2 h-4 w-4" />
                    <span>Subscription</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/settings")}>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => navigate("/admin/analytics")}
                      >
                        <BarChart3 className="mr-2 h-4 w-4 text-purple-600" />
                        <span className="text-purple-600 font-semibold">
                          Analytics Dashboard
                        </span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => navigate("/admin/subscribers")}
                      >
                        <Users className="mr-2 h-4 w-4 text-purple-600" />
                        <span className="text-purple-600 font-semibold">
                          Subscribers
                        </span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => navigate("/admin/permissions")}
                      >
                        <Shield className="mr-2 h-4 w-4 text-purple-600" />
                        <span className="text-purple-600 font-semibold">
                          Roles &amp; Permissions
                        </span>
                      </DropdownMenuItem>
                    </>
                  )}
                  {import.meta.env.VITE_DEV_MODE === "true" && (
                    <DropdownMenuItem
                      onClick={() => navigate("/system-settings")}
                    >
                      <Settings className="mr-2 h-4 w-4 text-yellow-600" />
                      <span className="text-yellow-600 font-semibold">
                        System Settings (Dev)
                      </span>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-red-600"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button variant="ghost" size="icon">
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
